import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type Ref,
} from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import type { BaseCheckboxProps } from './types';
import { useBaseFieldControl } from './internal/baseFieldContext';
import { resolveBaseAppearance } from './theme';
import { cn, hasNonEmptyRenderableNode, hasRenderableNode } from './utils';

const boxSizeClassNames = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
} as const;

const indicatorSizeClassNames = {
  sm: 'text-[10px]',
  md: 'text-[11px]',
  lg: 'text-xs',
} as const;

function setRefs<TElement>(
  element: TElement | null,
  ...refs: Array<Ref<TElement> | undefined>
) {
  for (const ref of refs) {
    if (!ref) continue;
    if (typeof ref === 'function') {
      ref(element);
      continue;
    }
    (ref as { current: TElement | null }).current = element;
  }
}

export const BaseCheckbox = forwardRef<HTMLInputElement, BaseCheckboxProps>(
  function BaseCheckbox(
    {
      label,
      description,
      indeterminate = false,
      invalid = false,
      disabled = false,
      controlSize,
      appearance,
      className,
      classNames,
      style,
      checked,
      defaultChecked,
      onBlur,
      onChange,
      'aria-invalid': nativeAriaInvalid,
      ...checkboxProps
    },
    forwardedRef,
  ) {
    for (const [name, value] of [
      ['indeterminate', indeterminate],
      ['invalid', invalid],
      ['disabled', disabled],
    ] as const) {
      if (typeof value !== 'boolean') {
        throw new TypeError(`BaseCheckbox ${name} must be a boolean.`);
      }
    }
    if (
      controlSize !== undefined &&
      controlSize !== 'sm' &&
      controlSize !== 'md' &&
      controlSize !== 'lg'
    ) {
      throw new TypeError(
        'BaseCheckbox controlSize must be "sm", "md", or "lg".',
      );
    }

    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);
    const fieldControl = useBaseFieldControl();
    const resolvedInvalid = invalid || fieldControl.invalid;
    const hasLabel = hasRenderableNode(label);
    const hasDescription = hasNonEmptyRenderableNode(description);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const indeterminateRef = useRef(indeterminate);
    indeterminateRef.current = indeterminate;
    const [uncontrolledChecked, setUncontrolledChecked] = useState(
      defaultChecked ?? false,
    );
    const isControlled = checked !== undefined;
    const resolvedChecked = Boolean(isControlled ? checked : uncontrolledChecked);
    const active = indeterminate || resolvedChecked;
    const state = indeterminate
      ? 'mixed'
      : resolvedChecked
        ? 'checked'
        : 'unchecked';

    const setInputRef = useCallback(
      (element: HTMLInputElement | null) => {
        inputRef.current = element;
        if (element) element.indeterminate = indeterminateRef.current;
        setRefs(element, forwardedRef);
      },
      [forwardedRef],
    );

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      if (!isControlled) {
        setUncontrolledChecked(event.currentTarget.checked);
      }
      onChange?.(event);
      fieldControl.notifyChange(event.currentTarget.checked);
      if (inputRef.current) {
        inputRef.current.indeterminate = indeterminate;
      }
    };

    return (
      <label
        {...componentAttributes}
        data-monkeys-component="checkbox"
        data-slot="root"
        data-state={state}
        data-size={controlSize}
        data-invalid={resolvedInvalid || undefined}
        data-disabled={disabled || undefined}
        className={cn(
          theme.slots.checkboxRoot,
          !hasDescription && 'items-center',
          disabled && theme.slots.checkboxDisabled,
          classNames?.root,
          className,
        )}
        style={{
          ...theme.styles.checkboxRoot,
          ...style,
        }}
      >
        <span
          data-slot="box"
          data-state={state}
          data-size={controlSize}
          className={cn(
            theme.slots.checkboxBox,
            !hasDescription && 'mt-0',
            active && theme.slots.checkboxBoxChecked,
            resolvedInvalid && theme.slots.checkboxBoxInvalid,
            controlSize && boxSizeClassNames[controlSize],
            classNames?.box,
          )}
          style={{
            ...theme.styles.checkboxBox,
          }}
        >
          <input
            {...checkboxProps}
            ref={setInputRef}
            type="checkbox"
            disabled={disabled}
            checked={checked}
            defaultChecked={defaultChecked}
            aria-invalid={nativeAriaInvalid ?? (resolvedInvalid || undefined)}
            aria-checked={indeterminate ? 'mixed' : resolvedChecked}
            data-slot="input"
            data-state={state}
            data-size={controlSize}
            data-invalid={resolvedInvalid || undefined}
            data-disabled={disabled || undefined}
            className={cn(theme.slots.checkboxInput, classNames?.input)}
            onBlur={(event) => {
              onBlur?.(event);
              fieldControl.notifyBlur(event.currentTarget.checked);
            }}
            onChange={handleChange}
          />
          {active ? (
            <span
              aria-hidden="true"
              data-slot="indicator"
              data-state={state}
              data-size={controlSize}
              className={cn(
                theme.slots.checkboxIndicator,
                controlSize && indicatorSizeClassNames[controlSize],
                classNames?.indicator,
              )}
            >
              {indeterminate ? '–' : '✓'}
            </span>
          ) : null}
        </span>
        {hasLabel || hasDescription ? (
          <span
            data-slot="content"
            className={cn(theme.slots.checkboxContent, classNames?.content)}
          >
            {hasLabel ? (
              <span
                data-slot="label"
                className={cn(theme.slots.checkboxLabel, classNames?.label)}
              >
                {label}
              </span>
            ) : null}
            {hasDescription ? (
              <span
                data-slot="description"
                className={cn(
                  theme.slots.checkboxDescription,
                  classNames?.description,
                )}
              >
                {description}
              </span>
            ) : null}
          </span>
        ) : null}
      </label>
    );
  },
);
